<template>
  <component
    v-bind="$attrs"
    :is="componentType"
    :class="containerClass"
    :link="link"
    :title="title"
    :target="$attrs?.target"
    :wrapper-component="isComponent ? 'LinkTileComponentWrapper' : undefined"
    @show-notify="setNotificationState"
    @set-icon-count="setIconCount"
    @set-notification-message="setNotificationMessage"
    @click="$emit('click')"
  >
    <div
      v-if="notify"
      :class="$style.iconWrapper"
      @click.prevent="setShowNotificationMessage(!showMessage)"
      @mouseenter="setShowNotificationMessage(true)"
      @mouseleave="setShowNotificationMessage(false)"
    >
      <NavigationIconNotification
        v-if="notify"
        :notify="notify"
        :is-interactive-tile="isInteractiveTile"
        :class="$style.notification"
      />
      <span
        v-if="showIconCount"
        :class="$style.iconCount"
        class="justify-center d-flex align-center"
        >{{ iconCount }}</span
      >
    </div>

    <p
      v-if="notificationMessage && showMessage"
      :class="[
        $style['notification-message'],
        { showMessageWithPropagation: [$style['show-notification-message']] },
      ]"
    >
      {{ notificationMessage }}
    </p>

    <IconSelect
      :class="[$style.icon, iconSizeClass]"
      :icon="iconSwitch"
      :svg-props="{ 'stroke-width': 1 }"
      :height="58"
      :width="58"
    />

    <BaseButton class="mx-0" :class="$style.link" :ripple="showRipplesOnClick">{{
      title
    }}</BaseButton>

    <IconSelect
      v-if="showNewBadge"
      icon="badge-new"
      :height="height"
      :width="width"
      :class="$style['badge-icon']"
      :viewBox="`0 0 ${height} ${width}`"
    />
  </component>
</template>

<script>
import { isUndefined } from 'lodash-es'
import { pascalCase } from 'change-case'
import API_CONSTANTS from '@/js/helper/api-constants.js'
import NavigationItemMixin from '@/js/components/Navigation/mixins/navigation-item-mixin.js'
import IconSelect from '@/js/components/Shared/IconSelect/IconSelect.vue'
import NavigationIconNotification from '@/js/components/Navigation/NavigationIconNotification.vue'
import NavigationComponents from '@/js/components/Navigation/components/navigation-routing-components.js'
import { fromUnixTime, isBefore } from 'date-fns'
import Icons from '@/js/components/Shared/IconSelect/Icons/index.js'

export default {
  name: 'LinkTile',
  components: {
    IconSelect,
    NavigationIconNotification,
    ...NavigationComponents,
  },
  mixins: [NavigationItemMixin],
  props: {
    size: {
      type: String,
      default: 'default',
    },
    renderType: {
      type: String,
      default: 'default',
    },
  },
  data() {
    return {
      renderComponent: true,
      notify: false,
      notificationMessage: undefined,
      iconCount: undefined,
      height: 445,
      width: 445,
      showMessage: false,
    }
  },
  computed: {
    isComponent() {
      return !!this[API_CONSTANTS.ICON.ATTRIBUTE.COMPONENT]
    },
    iconSwitch() {
      // Only use custom Tile icons, if included in switchable render types
      const switchableRenderTypes = ['default']
      let tileIconName = this.icon

      if (switchableRenderTypes.includes(this.renderType)) {
        tileIconName = `${pascalCase(this.icon)}TileIcon`
      }

      return Object.keys(Icons).indexOf(tileIconName) < 0 ? this.icon : `${this.icon}-tile`
    },
    showIconCount() {
      return !isUndefined(this.iconCount)
    },
    isInteractiveTile() {
      return this.$props.type === 'LinkTile'
    },
    showNewBadge() {
      let showBadge = false

      if (this.badgeDate) {
        const date = fromUnixTime(this.badgeDate)
        showBadge = !!date && !!isBefore(new Date(), date)
      }

      return showBadge
    },
    iconSizeClass() {
      switch (this.size) {
        case 'big':
          return this.$style.iconSizeLarge
        default:
          return this.$style.iconSizeDefault
      }
    },
    containerClass() {
      switch (this.renderType) {
        case 'navigationLink':
          return this.$style.navigationLinkContainer
        case 'navigationMobileLink':
          return [this.$style.navigationLinkContainer, this.$style.navigationLinkContainerMobile]
        default:
          return this.$style.container
      }
    },
    showRipplesOnClick() {
      const blacklist = ['navigationLink', 'navigationMobileLink']

      return !blacklist.includes(this.renderType)
    },
  },
  methods: {
    setNotificationState(state) {
      this.notify = state
    },
    setIconCount(count) {
      this.iconCount = count
    },
    setNotificationMessage(message) {
      this.notificationMessage = message
    },
    setShowNotificationMessage(boolean) {
      this.showMessage = boolean
    },
  },
}
</script>

<style lang="scss" module>
.container {
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  font-size: inherit;

  border: var(--border-grey-lighten-1);

  transition: var(--transition-primary);

  &:hover {
    text-decoration: none;

    box-shadow: 0px 5px 15px 0 hsla(0, 0%, 0%, 0.15);
  }
}

.notification-message:hover {
  background: cadetblue;
}

.notification-message {
  position: absolute;
  top: -16px;
  z-index: 2;

  margin-bottom: 0 !important;
  padding: 6px;

  color: var(--white);

  font-size: 14px;
  letter-spacing: normal;
  text-transform: initial;

  background: var(--red-lighten-2);
  border-radius: 5px;

  pointer-events: all;
}

.iconWrapper {
  @media screen and (max-width: map-get($grid-breakpoints, 'sm')) {
    margin-right: 0.5rem;
  }

  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;

  padding: 1.2rem;

  pointer-events: all;
}

.icon {
  width: 3.6875em;
  height: 3.6875em;

  color: var(--grey);
}

.badge-icon {
  position: absolute;
  top: 10px;
  left: 10px;

  width: 3.6rem;
  height: 3.6rem;

  transform: rotate(-35deg);

  fill: var(--yellow);
}

.link {
  @media screen and (max-width: map-get($grid-breakpoints, 'sm')) {
    width: 70%;
  }
  min-width: 60%;
  margin-top: 1.3rem;
  padding: 0.75rem 1rem !important;

  &:global(.v-btn.v-btn.v-size--default) {
    :global(.v-btn__content) {
      font-size: var(--font-base);
      text-transform: none;
    }
  }
}

.notification.notification,
.iconCount {
  width: var(--font-2xl);
  height: var(--font-2xl);

  font-size: var(--font-2xl);

  transition: all 200ms ease;
}

.notification {
  position: inherit;
  top: 20%;
}

.iconCount {
  color: var(--white);
  font-size: var(--font-sm);

  background: var(--primary);
  border-radius: 50%;
}

.iconSizeLarge {
  width: 12.5rem;
  height: 12.5rem;
}

.iconSizeDefault {
  width: 3.6875em;
  height: 3.6875em;
}

// Overwrite Styling for nav link container

.navigationLinkContainer {
  position: relative;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;

  font-size: inherit;

  border-bottom: var(--border-grey-lighten-1);

  .icon {
    margin-right: 0.75rem;
  }

  .iconSizeDefault,
  .iconSizeLarge {
    width: 2rem;
    height: 2rem;
  }

  .iconWrapper {
    top: 8px;
    left: 36px;

    width: 1px;
    padding: 0;
  }

  .badge-icon {
    position: relative;

    top: inherit;
    left: inherit;

    width: 2.5rem;
    height: 2.5rem;
  }

  &:hover {
    text-decoration: none;
  }

  .notification {
    position: relative;
  }

  :global(.v-btn) {
    margin: 0 !important;
  }

  // Overwrite Link tile links

  button[type='primary'] {
    flex-grow: 1;

    color: var(--text) !important;

    background-color: white !important;
    border: none !important;
  }

  // Vuetify overwrite

  :global(.v-btn__content) {
    flex-direction: row !important;
    align-items: center;
    justify-content: flex-start;
  }
}

// Overwrite Link tile styles for mobile navigation

.navigationLinkContainerMobile {
  border: none;

  :global(.v-btn__content) {
    font-size: 14px !important;

    &:hover {
      text-decoration: underline !important;
    }
  }

  :global(.btn.v-btn:not(.link)) {
    &:hover {
      text-decoration: underline !important;
    }
  }

  :global(.v-btn) {
    min-height: 18px;

    font-weight: 400;
    line-height: 1;
    letter-spacing: normal;
  }

  .iconWrapper {
    // top: -10px;
    left: 20px;
  }

  .link {
    flex: 0 0 max-content !important;
  }
}
</style>
